import { Link } from '@mui/material';
import { i18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import { I18nProvider } from '@lingui/react';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  activateLanguage,
  getTypeTranslation
} from '../../utils/languageSupport';

const BrowseByTypeComponent = ({
  language,
  bannerBoxData,
  productDocumentation,
  baseUrl,
  product,
  returnToContext,
  typesData
}) => {
  const [browseByTypeData, setbrowseByTypeData] = useState({});

  useEffect(() => {
    activateLanguage(language);
    const data = [
      {
        title: t`Hotfixes and updates`,
        description: t`Obtain the latest product enhancements.`,
        slug: 'hotfixes',
        value: getTypeTranslation(language, 'hotfixes')
      },
      {
        title: t`Utilities`,
        description: t`Obtain utilities specially designed for your product.`,
        slug: 'utilities',
        value: getTypeTranslation(language, 'utilities')
      },
      {
        title: t`Offline Help`,
        description: t`Download offline Help files to use when you can’t be online.`,
        slug: 'offlineHelp',
        value: getTypeTranslation(language, 'offlineHelp')
      },
      {
        title: t`Templates`,
        description: t`Get templates to create new projects with standardized properties.`,
        slug: 'templates',
        value: getTypeTranslation(language, 'templates')
      },
      {
        title: t`Object Enablers`,
        description: t`View non-standard objects outside of the product in which they were created.`,
        slug: 'objectEnablers',
        value: getTypeTranslation(language, 'objectEnablers')
      }
    ];

    const types = typesData;
    let typeArray = [];
    if (types !== undefined && types !== null) {
      typeArray = data.filter((a) =>
        types.some((b) => a.value === b.value && b.count > 1)
      );
    }
    if (productDocumentation !== undefined && productDocumentation.title) {
      typeArray = [productDocumentation, ...typeArray];
    }

    setbrowseByTypeData(typeArray);
  }, [language, typesData, productDocumentation]);

  return (
    <I18nProvider i18n={i18n}>
      <div>
        <h2 className="dhig-typography-headline-medium dhig-mb-2 dhig-mt-0 browse-by-type-section-title">
          <Trans>Get the product components you need</Trans>
        </h2>
        <p className="dhig-typography-body-copy-medium dhig-mb-7 dhig-mt-0">
          {t`Get essential ${bannerBoxData?.productName} components.`}
        </p>
        <div className="dhig-flex dhig-flex-wrap">
          {browseByTypeData.length > 0 &&
            browseByTypeData.map((item, index) => (
              <div
                key={item.title}
                className="jest-item dhig-mb-7 dhig-w-full sm:dhig-w-full md:dhig-w-1/2 lg:dhig-w-1/3"
              >
                <div
                  className={
                    index === browseByTypeData.length - 1
                      ? ''
                      : 'md:dhig-mr-4 lg:dhig-mr-5'
                  }
                >
                  <p className="dhig-typography-headline-smaller dhig-mb-0 dhig-mt-0">
                    <Link
                      href={
                        item.url
                          ? encodeURI(item.url)
                          : encodeURI(
                              `${baseUrl}?qt=&type=${getTypeTranslation(
                                language,
                                item.slug
                              )}&p=${product}${returnToContext}`
                            )
                      }
                      data-wat-loc="browse categories"
                      data-wat-val={item.title.toLowerCase()}
                    >
                      {item.title}
                    </Link>
                  </p>

                  <p className="dhig-typography-body-copy-medium dhig-mb-0 dhig-mt-0">
                    {item.description ? (
                      item.description
                    ) : (
                      <Trans>
                        Get detailed online Help for using your software.
                      </Trans>
                    )}
                  </p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </I18nProvider>
  );
};

export default BrowseByTypeComponent;

BrowseByTypeComponent.propTypes = {
  /**
   * language prop
   */
  language: PropTypes.string,
  /**
   * data for the current product
   */
  bannerBoxData: PropTypes.shape({
    productName: PropTypes.string.isRequired
  }).isRequired,
  /**
   * documentation array
   */
  productDocumentation: PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
    description: PropTypes.string.isRequired,
    listCode: PropTypes.string
  }).isRequired,
  /**
   * baseUrl for navigation
   */
  baseUrl: PropTypes.string.isRequired,
  /**
   * current product name
   */
  product: PropTypes.string.isRequired,
  /**
   * link postfix for return to context navigation
   */
  returnToContext: PropTypes.string.isRequired,
  /**
   * types filter data
   */
  typesData: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      count: PropTypes.number
    })
  )
};

BrowseByTypeComponent.defaultProps = {
  language: '',
  typesData: undefined
};
